/**
 * Path maths for the "stardust warp" — Maya's launcher bursting into stars
 * that fly to the sidebar's avatar (and back). Pure functions so the motion
 * can be tested without a canvas; maya-warp.tsx draws what these return.
 */

export interface Point {
  x: number;
  y: number;
}

export interface WarpStar {
  /** Direction of the initial burst away from the source (radians). */
  angle: number;
  /** How far the burst throws the star before it turns for the target (px). */
  burst: number;
  /** Start offset (ms) — stars leave in a ripple, not all at once. */
  delay: number;
  /** Time from leaving to landing (ms). */
  duration: number;
  /** Sideways bow of the flight path, as a fraction of its length (±). */
  bend: number;
  /** Radius at full size (px). */
  size: number;
  /** Total rotation over the flight (radians). */
  spin: number;
  color: string;
  shape: "sparkle" | "dot";
}

export interface StarFrame extends Point {
  /** 0–1, multiplies `size`. */
  scale: number;
  alpha: number;
  rotation: number;
}

/** Centre of an element on screen, or null if it isn't laid out. */
export const centreOf = (el: Element | null): Point | null => {
  const r = el?.getBoundingClientRect();
  return r && r.width > 0
    ? { x: r.left + r.width / 2, y: r.top + r.height / 2 }
    : null;
};

/** Share of a star's flight spent bursting outward before it turns. */
export const BURST_FRACTION = 0.24;

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

const lerp = (a: Point, b: Point, t: number): Point => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});

const quadBezier = (p0: Point, c: Point, p1: Point, t: number): Point => {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * c.x + t * t * p1.x,
    y: u * u * p0.y + 2 * u * t * c.y + t * t * p1.y,
  };
};

export const createStars = (
  count: number,
  colors: string[],
  rand: () => number = Math.random,
): WarpStar[] =>
  Array.from({ length: count }, (_, i) => ({
    // Evenly spread burst with jitter, so the swarm reads as an explosion.
    angle: (i / count) * Math.PI * 2 + (rand() - 0.5) * 0.6,
    burst: 26 + rand() * 46,
    delay: rand() * 140,
    duration: 600 + rand() * 220,
    bend: (rand() - 0.5) * 0.9,
    size: 2.5 + rand() * 3.5,
    spin: (rand() - 0.5) * Math.PI * 4,
    color: colors[i % colors.length],
    shape: rand() < 0.7 ? "sparkle" : "dot",
  }));

/** When the last star lands (ms after the warp starts). */
export const warpDuration = (stars: WarpStar[]): number =>
  stars.reduce((max, s) => Math.max(max, s.delay + s.duration), 0);

/**
 * Where a star is `elapsed` ms into the warp: a short burst away from `from`,
 * then a bowed flight into `to`. `to` may move between frames (the sidebar is
 * still sliding open) — the path is recomputed from it every time, so the
 * swarm curves after a moving target. Null before the star leaves and after
 * it has landed.
 */
export const starAt = (
  star: WarpStar,
  elapsed: number,
  from: Point,
  to: Point,
): StarFrame | null => {
  const p = (elapsed - star.delay) / star.duration;
  if (p < 0 || p >= 1) return null;

  const burstPoint = {
    x: from.x + Math.cos(star.angle) * star.burst,
    y: from.y + Math.sin(star.angle) * star.burst,
  };

  let pos: Point;
  let scale: number;
  if (p < BURST_FRACTION) {
    const q = easeOutCubic(p / BURST_FRACTION);
    pos = lerp(from, burstPoint, q);
    scale = 0.3 + 0.7 * q;
  } else {
    const q = easeInOutCubic((p - BURST_FRACTION) / (1 - BURST_FRACTION));
    const dx = to.x - burstPoint.x;
    const dy = to.y - burstPoint.y;
    // Control point pushed off the straight line, perpendicular to it.
    const control = {
      x: (burstPoint.x + to.x) / 2 - dy * star.bend,
      y: (burstPoint.y + to.y) / 2 + dx * star.bend,
    };
    pos = quadBezier(burstPoint, control, to, q);
    scale = 1 - 0.65 * q;
  }

  return {
    ...pos,
    scale,
    alpha: p > 0.9 ? (1 - p) / 0.1 : 1,
    rotation: star.spin * p,
  };
};
